import { useState, useEffect } from "react";
import { useParams, Link } from "react-router-dom";
import axios from "axios";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Separator } from "@/components/ui/separator";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Globe, MapPin, Mail, Phone, ExternalLink, Star, Quote, Building2, Search, CalendarDays, Image, Newspaper, ChevronLeft, ChevronRight, Loader2, ArrowLeft, Clock, Eye } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function OrgPublicProfile() {
  const { slug } = useParams();
  const [profile, setProfile] = useState(null);
  const [news, setNews] = useState({ news: [], total: 0 });
  const [fairs, setFairs] = useState([]);
  const [galleries, setGalleries] = useState({ galleries: [], total: 0 });
  const [testimonials, setTestimonials] = useState([]);
  const [newsPage, setNewsPage] = useState(1);
  const [galPage, setGalPage] = useState(1);
  const [fairPage, setFairPage] = useState(1);
  const [fairsAll, setFairsAll] = useState([]);
  const [selectedNews, setSelectedNews] = useState(null);
  const [searchQ, setSearchQ] = useState("");
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    axios.get(`${API}/organizers/${slug}`).then(r => setProfile(r.data)).catch(() => setProfile(null)).finally(() => setLoading(false));
  }, [slug]);

  const fetchNews = (p = 1) => { axios.get(`${API}/organizers/${slug}/news?page=${p}`).then(r => { setNews(r.data); setNewsPage(p); }); };
  const fetchFairs = () => { axios.get(`${API}/organizers/${slug}/fairs`).then(r => setFairsAll(r.data)); };
  const fetchGalleries = (p = 1) => { axios.get(`${API}/organizers/${slug}/galleries?page=${p}`).then(r => { setGalleries(r.data); setGalPage(p); }); };
  const fetchTestimonials = () => { axios.get(`${API}/organizers/${slug}/testimonials`).then(r => setTestimonials(r.data)); };

  if (loading) return <div className="flex justify-center py-32"><Loader2 className="w-8 h-8 animate-spin text-stone-300" /></div>;
  if (!profile) return <div className="text-center py-32"><h1 className="text-3xl font-bold mb-4" style={{ fontFamily: "'Playfair Display', serif" }}>Organizatör Bulunamadı</h1><Link to="/" className="text-[#1B4F72] hover:underline">Ana sayfaya dön</Link></div>;

  const filteredNews = searchQ ? news.news.filter(n => n.title.toLowerCase().includes(searchQ.toLowerCase()) || (n.content||"").toLowerCase().includes(searchQ.toLowerCase())) : news.news;

  return (
    <div data-testid="org-profile" className="bg-white min-h-screen">
      {/* Hero */}
      <section className="bg-stone-950 text-white relative overflow-hidden">
        {profile.cover_image_url && <div className="absolute inset-0"><img src={profile.cover_image_url} alt="" className="w-full h-full object-cover opacity-30" /></div>}
        <div className="relative max-w-screen-lg mx-auto px-6 md:px-12 py-20 md:py-28 flex items-center gap-8">
          <div className="w-24 h-24 md:w-32 md:h-32 bg-white rounded-sm flex items-center justify-center flex-shrink-0 shadow-xl">
            {profile.logo_url ? <img src={profile.logo_url} alt="" className="w-full h-full object-contain p-3" /> : <Globe className="w-10 h-10 text-stone-300" />}
          </div>
          <div>
            <h1 className="text-3xl md:text-5xl font-bold tracking-tight" style={{ fontFamily: "'Playfair Display', serif" }}>{profile.org_name}</h1>
            {profile.contact_city && <p className="text-stone-400 mt-3 flex items-center gap-1.5 text-sm"><MapPin className="w-4 h-4" />{profile.contact_city}{profile.contact_country ? `, ${profile.contact_country}` : ""}</p>}
            {profile.about_short && <p className="text-stone-300 mt-2 text-sm max-w-xl">{profile.about_short}</p>}
          </div>
        </div>
      </section>

      {/* Contact Bar */}
      <section className="bg-stone-50 border-b border-stone-200">
        <div className="max-w-screen-lg mx-auto px-6 md:px-12 py-4 flex flex-wrap items-center gap-6 text-sm text-stone-600">
          {profile.contact_email && <a href={`mailto:${profile.contact_email}`} className="flex items-center gap-2 hover:text-[#1B4F72]"><Mail className="w-4 h-4 text-stone-400" />{profile.contact_email}</a>}
          {profile.contact_phone && <a href={`tel:${profile.contact_phone}`} className="flex items-center gap-2 hover:text-[#1B4F72]"><Phone className="w-4 h-4 text-stone-400" />{profile.contact_phone}</a>}
          {profile.website && <a href={profile.website} target="_blank" rel="noopener noreferrer" className="flex items-center gap-2 hover:text-[#1B4F72]"><ExternalLink className="w-4 h-4 text-stone-400" />{profile.website}</a>}
          {/* Search */}
          <div className="ml-auto relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-stone-400" />
            <Input value={searchQ} onChange={e => setSearchQ(e.target.value)} placeholder="Bu profilde ara..." className="pl-9 h-8 rounded-md text-xs w-48 border-stone-300" data-testid="org-profile-search" />
          </div>
        </div>
      </section>

      {/* Tabs */}
      <div className="max-w-screen-lg mx-auto px-6 md:px-12 py-8">
        <Tabs defaultValue="about" onValueChange={v => { if (v === "news") fetchNews(); if (v === "fairs") fetchFairs(); if (v === "galleries") fetchGalleries(); if (v === "testimonials") fetchTestimonials(); }}>
          <TabsList className="bg-transparent border-b border-stone-200 rounded-none p-0 h-auto gap-0">
            {[{v:"about",l:"Hakkında",i:Building2},{v:"news",l:"Haberler",i:Newspaper},{v:"fairs",l:"Fuar Takvimi",i:CalendarDays},{v:"galleries",l:"Galeri",i:Image},{v:"testimonials",l:"Yorumlar",i:Star}].map(t => (
              <TabsTrigger key={t.v} value={t.v} className="rounded-none border-b-2 border-transparent data-[state=active]:border-[#1B4F72] data-[state=active]:text-[#1B4F72] font-mono text-[10px] uppercase tracking-widest px-4 py-3 gap-1.5"><t.i className="w-3.5 h-3.5" />{t.l}</TabsTrigger>
            ))}
          </TabsList>

          {/* ABOUT */}
          <TabsContent value="about" className="py-8 space-y-8">
            {profile.about_long && <div className="text-stone-700 leading-relaxed whitespace-pre-wrap text-lg max-w-3xl">{profile.about_long}</div>}
            {profile.contact_person && <div><span className="font-mono text-[10px] uppercase tracking-widest text-[#1B4F72]">Yetkili Kişi</span><p className="text-stone-900 font-semibold mt-1">{profile.contact_person}</p></div>}
            {profile.contact_address && <div><span className="font-mono text-[10px] uppercase tracking-widest text-[#1B4F72]">Adres</span><p className="text-stone-600 mt-1">{profile.contact_address}</p></div>}
          </TabsContent>

          {/* NEWS */}
          <TabsContent value="news" className="py-8 space-y-6">
            {selectedNews ? (
              /* News Detail View */
              <div>
                <button onClick={() => setSelectedNews(null)} className="flex items-center gap-2 text-sm text-stone-500 hover:text-[#1B4F72] mb-6 font-mono uppercase tracking-widest">
                  <ArrowLeft className="w-4 h-4" /> Haberlere Dön
                </button>
                {selectedNews.image_url && (
                  <div className="aspect-[21/9] max-w-3xl overflow-hidden mb-6">
                    <img src={selectedNews.image_url} alt="" className="w-full h-full object-cover" />
                  </div>
                )}
                <h2 className="text-2xl md:text-3xl font-bold text-stone-900 mb-3" style={{ fontFamily: "'Playfair Display', serif" }}>{selectedNews.title}</h2>
                <div className="flex items-center gap-4 text-sm text-stone-500 mb-6 font-mono text-xs">
                  <span className="flex items-center gap-1"><Clock className="w-3.5 h-3.5" />{new Date(selectedNews.created_at).toLocaleDateString("tr-TR", { day: "numeric", month: "long", year: "numeric" })}</span>
                </div>
                <Separator className="mb-6" />
                <div className="max-w-3xl text-stone-700 leading-relaxed text-lg whitespace-pre-wrap">{selectedNews.content}</div>
                {selectedNews.gallery_images?.length > 0 && (
                  <div className="grid grid-cols-4 gap-2 mt-8 max-w-3xl">
                    {selectedNews.gallery_images.map((img, i) => (
                      <div key={i} className="aspect-[4/3] overflow-hidden"><img src={img} alt="" className="w-full h-full object-cover" /></div>
                    ))}
                  </div>
                )}
              </div>
            ) : (
              /* News List View */
              <>
                {filteredNews.length > 0 ? filteredNews.map(n => (
                  <button key={n.id} onClick={() => setSelectedNews(n)} className="w-full text-left border-b border-stone-200 pb-6 hover:bg-stone-50 -mx-3 px-3 py-2 transition-colors group" data-testid={`org-news-${n.id}`}>
                    <div className="flex gap-4">
                      {n.image_url && <img src={n.image_url} alt="" className="w-40 h-28 object-cover flex-shrink-0" />}
                      <div>
                        <h3 className="text-lg font-bold text-stone-900 group-hover:text-[#1B4F72] transition-colors" style={{ fontFamily: "'Playfair Display', serif" }}>{n.title}</h3>
                        <p className="text-sm text-stone-500 mt-1 line-clamp-3">{n.summary || n.content}</p>
                        <span className="font-mono text-[9px] text-stone-400 mt-2 block uppercase tracking-widest">{new Date(n.created_at).toLocaleDateString("tr-TR", { day: "numeric", month: "long", year: "numeric" })} · Devamını oku →</span>
                      </div>
                    </div>
                  </button>
                )) : <p className="text-stone-400 text-center py-8">Henüz haber yok</p>}
                {news.total > 5 && <div className="flex justify-center gap-2">
                  <Button onClick={() => fetchNews(newsPage-1)} disabled={newsPage<=1} variant="outline" size="sm" className="rounded-none">Önceki</Button>
                  <span className="text-xs text-stone-500 py-2">{newsPage}/{Math.ceil(news.total/5)}</span>
                  <Button onClick={() => fetchNews(newsPage+1)} disabled={newsPage>=Math.ceil(news.total/5)} variant="outline" size="sm" className="rounded-none">Sonraki</Button>
                </div>}
              </>
            )}
          </TabsContent>

          {/* FAIRS */}
          <TabsContent value="fairs" className="py-8 space-y-4">
            {fairsAll.length > 0 ? (() => {
              const totalPages = Math.ceil(fairsAll.length / 5);
              const paginated = fairsAll.slice((fairPage - 1) * 5, fairPage * 5);
              return (
                <>
                  {paginated.map(f => (
                    <div key={f.id} className="bg-stone-50 border border-stone-200 p-5">
                      <div className="flex items-start justify-between">
                        <div>
                          <h3 className="text-lg font-bold" style={{ fontFamily: "'Playfair Display', serif" }}>{f.name}</h3>
                          <div className="flex flex-wrap gap-3 mt-1 text-sm text-stone-500">
                            {f.topic && <Badge variant="outline" className="rounded-none text-[9px]">{f.topic}</Badge>}
                            {f.city && <span className="flex items-center gap-1"><MapPin className="w-3 h-3" />{f.city}{f.country ? `, ${f.country}` : ""}</span>}
                            {f.start_date && <span className="flex items-center gap-1"><CalendarDays className="w-3 h-3" />{f.start_date} — {f.end_date}</span>}
                          </div>
                        </div>
                        {f.website && <a href={f.website} target="_blank" rel="noopener noreferrer" className="text-[#1B4F72] hover:underline text-xs flex items-center gap-1"><ExternalLink className="w-3 h-3" />Web</a>}
                      </div>
                      {f.short_desc && <p className="text-sm text-stone-600 mt-3">{f.short_desc}</p>}
                      {f.long_desc && <p className="text-sm text-stone-500 mt-2 leading-relaxed">{f.long_desc}</p>}
                    </div>
                  ))}
                  {totalPages > 1 && (
                    <div className="flex justify-center items-center gap-1 mt-6">
                      <Button onClick={() => setFairPage(p => Math.max(1, p-1))} disabled={fairPage <= 1} variant="outline" size="sm" className="rounded-none h-8 w-8 p-0">
                        <ChevronLeft className="w-4 h-4" />
                      </Button>
                      {Array.from({length: totalPages}, (_, i) => i + 1).map(p => (
                        <Button key={p} onClick={() => setFairPage(p)} variant={fairPage === p ? "default" : "outline"} size="sm"
                          className={`rounded-none h-8 w-8 p-0 text-xs ${fairPage === p ? "bg-[#1B4F72] text-white" : ""}`}>
                          {p}
                        </Button>
                      ))}
                      <Button onClick={() => setFairPage(p => Math.min(totalPages, p+1))} disabled={fairPage >= totalPages} variant="outline" size="sm" className="rounded-none h-8 w-8 p-0">
                        <ChevronRight className="w-4 h-4" />
                      </Button>
                    </div>
                  )}
                </>
              );
            })() : <p className="text-stone-400 text-center py-8">Henüz fuar eklenmemiş</p>}
          </TabsContent>

          {/* GALLERIES */}
          <TabsContent value="galleries" className="py-8 space-y-8">
            {galleries.galleries.length > 0 ? galleries.galleries.map(g => (
              <div key={g.id}>
                <h3 className="font-bold text-lg mb-1" style={{ fontFamily: "'Playfair Display', serif" }}>{g.fair_name}</h3>
                <p className="text-sm text-stone-500 mb-3">{g.city}{g.country ? `, ${g.country}` : ""} · {g.start_date} — {g.end_date}</p>
                <div className="grid grid-cols-4 gap-2">
                  {(g.images||[]).map((img, i) => (<div key={i} className="aspect-[4/3] overflow-hidden"><img src={img} alt="" className="w-full h-full object-cover hover:scale-105 transition-transform" /></div>))}
                </div>
              </div>
            )) : <p className="text-stone-400 text-center py-8">Henüz galeri yok</p>}
            {galleries.total > 5 && <div className="flex justify-center gap-2">
              <Button onClick={() => fetchGalleries(galPage-1)} disabled={galPage<=1} variant="outline" size="sm" className="rounded-none">Önceki</Button>
              <span className="text-xs text-stone-500 py-2">{galPage}/{Math.ceil(galleries.total/5)}</span>
              <Button onClick={() => fetchGalleries(galPage+1)} disabled={galPage>=Math.ceil(galleries.total/5)} variant="outline" size="sm" className="rounded-none">Sonraki</Button>
            </div>}
          </TabsContent>

          {/* TESTIMONIALS */}
          <TabsContent value="testimonials" className="py-8">
            {testimonials.length > 0 ? <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              {testimonials.map(t => (
                <div key={t.id} className="relative pl-6 border-l-2 border-[#1B4F72]">
                  <p className="text-stone-700 italic leading-relaxed text-lg">"{t.text}"</p>
                  <div className="mt-3 flex items-center gap-3">
                    <div className="flex">{[1,2,3,4,5].map(s => <Star key={s} className={`w-3.5 h-3.5 ${s <= (t.rating||5) ? "text-[#D4AF37] fill-[#D4AF37]" : "text-stone-200"}`} />)}</div>
                    <span className="text-sm font-semibold">{t.author}</span>
                    {t.company && <span className="text-sm text-stone-400">— {t.company}</span>}
                  </div>
                </div>
              ))}
            </div> : <p className="text-stone-400 text-center py-8">Henüz yorum yok</p>}
          </TabsContent>
        </Tabs>
      </div>

      {/* CTA */}
      <section className="bg-stone-950 text-white">
        <div className="max-w-screen-lg mx-auto px-6 md:px-12 py-16 text-center">
          <h2 className="text-2xl md:text-3xl font-bold mb-4" style={{ fontFamily: "'Playfair Display', serif" }}>{profile.org_name} ile İletişime Geçin</h2>
          {profile.contact_email && <a href={`mailto:${profile.contact_email}`} className="inline-flex items-center gap-2 px-8 py-3 bg-[#1B4F72] hover:bg-[#154360] text-white font-mono text-xs uppercase tracking-widest transition-colors mt-4"><Mail className="w-4 h-4" /> E-posta Gönder</a>}
        </div>
      </section>
    </div>
  );
}
